import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import i18next from "i18next";
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player'
import Moment from 'react-moment';
import 'moment/locale/fr';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Chip from '@material-ui/core/Chip';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import IconButton from '@material-ui/core/Button';

import httpClient from '../_shared/HttpClient';
import ScrollBody from '../_shared/ScrollBody';
import Loader from '../_shared/Loader';
import { convertMillisToTime } from '../_shared/StringFormat';
import EventComments from './EventComments';

//store
import { appStore } from '../../appStore.js';

const useStyles = makeStyles(theme => ({
    tabs: {
        marginBottom: theme.spacing(1),
    },
    teamAvatar: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarCaption: {
        fontSize: '60%',
        fontStyle: 'italic',
    },
    title: {
        fontSize: '110%',
    },
    subtitle: {
        paddingTop: -3,
        marginTop: -3,
        fontSize: '85%',
        fontStyle: 'italic',
    },
    smallAvatar: {
        width: props => theme.spacing(4),
        height: props => theme.spacing(4),
    },
    playerLinkText: {
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        fontWeight: 500,
    },
}));

const Event = (props) => {
    const { state, dispatch } = useContext(appStore);
    const { leagueID, gameID, eventID } = props.match.params;
    const { t } = useTranslation();
    const classes = useStyles(state);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [data, setData] = useState(null);

    useEffect(() => {
        dispatch({ type: 'updateNavigation', payload: { navTitle: 'common:event' } });
        return () => {
        }
    }, []);

    useEffect(() => {
        props.setLeagueID(leagueID);
        if (!eventID) return;
        loadData();
    }, [gameID, eventID]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            var result = await httpClient('get', 'Calendar/GetEventAndVideos', { id: eventID });
            setData(result);
        }
        catch (err) {
            console.log(err);
        }
        setIsLoading(false);
    };

    const normalizeVideoUrl = (value) => {
        if (!value) return '';
        const raw = String(value).trim();
        if (!raw) return '';

        if (raw.startsWith('https://')) {
            return raw;
        }

        if (raw.startsWith('http://')) {
            return `https://${raw.substring('http://'.length)}`;
        }

        if (raw.startsWith('//')) {
            return `https:${raw}`;
        }

        if (raw.startsWith('/')) {
            return raw;
        }

        const firstSlashIndex = raw.indexOf('/');
        const head = firstSlashIndex > 0 ? raw.substring(0, firstSlashIndex) : raw;
        if (head.indexOf('.') >= 0) {
            return `https://${raw}`;
        }

        return `https://syncstats.ca/${raw.replace(/^\/+/, '')}`;
    };

    const inferVideoMimeType = (url) => {
        if (!url) return 'video/mp4';
        const cleanUrl = url.split('?')[0].toLowerCase();
        if (cleanUrl.endsWith('.m3u8')) return 'application/x-mpegURL';
        if (cleanUrl.endsWith('.webm')) return 'video/webm';
        if (cleanUrl.endsWith('.ogg') || cleanUrl.endsWith('.ogv')) return 'video/ogg';
        return 'video/mp4';
    };

    const getPlayerPath = (playerID) => playerID ? `/home/calendar/${leagueID}/player/${playerID}` : null;
    const selectedVideoUrl = data && data.cameras && data.cameras[selectedTabIndex]
        ? normalizeVideoUrl(data.cameras[selectedTabIndex].videoUrl)
        : '';
    const selectedVideoSources = selectedVideoUrl
        ? [{ src: selectedVideoUrl, type: inferVideoMimeType(selectedVideoUrl) }]
        : [];

    if (isLoading) {
        return <Loader isLoading={isLoading}><div></div></Loader>
    }

    return (
        <>
            <List disablePadding>
                <ListItem dense disableGutters divider>
                    <ListItemAvatar>
                        <div className={classes.teamAvatar}>
                            <Avatar className={classes.smallAvatar} component={Link} to={`/home/calendar/${leagueID}/summary/${gameID}`}>
                                <BackIcon />
                            </Avatar>
                        </div>
                    </ListItemAvatar>
                    <ListItemText
                        primary={data ? data.gameTitle : ''}
                        secondary={
                            <Moment locale={i18next.language} format="ddd Do MMMM YYYY - HH:mm" date={data ? data.gameDateTime : ''} />
                        } />
                    <>
                        <ButtonGroup size='small'>
                            <IconButton
                                disabled={!data || !data.previousEventID}
                                component={Link} to={`/home/calendar/${leagueID}/summary/${gameID}/event/${(data ? data.previousEventID : eventID)}`}
                            >
                                <NavigateBeforeIcon />
                            </IconButton>

                            <IconButton
                                disabled={!data || !data.nextEventID}
                                component={Link} to={`/home/calendar/${leagueID}/summary/${gameID}/event/${(data ? data.nextEventID : eventID)}`}
                            >
                                <NavigateNextIcon />
                            </IconButton>
                        </ButtonGroup>
                    </>
                </ListItem>
            </List>
            
            <ScrollBody>
                <Grid
                    container
                    alignItems="flex-start"
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Tabs
                            value={selectedTabIndex}
                            onChange={(e, v) => setSelectedTabIndex(v)}
                            variant={"scrollable"}
                            className={classes.tabs}
                        >
                            {
                                (data && data.cameras ? data.cameras : []).map((x, i) =>
                                    <Tab
                                        key={i}
                                        label={'CAM ' + (i + 1)}
                                    />
                                )
                            }
                        </Tabs >

                        <ReactPlayer
                            style={{ margin: 0, padding: 0 }}
                            width={state.isMobile ? state.width * .9 : ''}
                            height={state.isMobile ? '100%' : ''}
                            url={selectedVideoSources}
                            controls
                            playing={false}
                            playsinline
                            config={{
                                file: {
                                    attributes: {
                                        playsInline: true,
                                        preload: 'metadata',
                                        controlsList: 'nodownload'
                                    }
                                }
                            }}
                        />

                        <List>
                            <ListItem
                                divider
                                dense
                                disableGutters
                            >
                                <ListItemAvatar>
                                        <Avatar
                                            src={data.team ? data.team.logoUrl : ''}
                                            className={classes.smallAvatar}
                                        />
                                </ListItemAvatar>

                                <ListItemText
                                    primary={
                                        <span>
                                            {data && data.playerID && data.playerName ? (
                                                <span
                                                    className={classes.playerLinkText}
                                                >
                                                    <Link
                                                        to={getPlayerPath(data.playerID) || '#'}
                                                        className={classes.playerLinkText}
                                                    >
                                                        {`${data.playerName}${data.playerDefaultNumber ? ' #' + data.playerDefaultNumber : ''}`}
                                                    </Link>
                                                </span>
                                            ) : (data ? data.title : '')}
                                            {data && data.team ? ` (${data.team.name})` : ''}
                                        </span>
                                    }
                                    secondary={
                                        data ? (
                                            <span>
                                                {data.subtitle ? data.subtitle : ''}
                                                {(data.assistPlayers && data.assistPlayers.length > 0) ? (
                                                    <>
                                                        {data.subtitle ? ' | ' : ''}
                                                        {t('actions:assist')}
                                                        {data.assistPlayers.map((ap, index) => (
                                                            <React.Fragment key={`event_assist_${ap.playerID || index}`}>
                                                                {index > 0 ? ', ' : ''}
                                                                <span
                                                                    className={classes.playerLinkText}
                                                                >
                                                                    <Link
                                                                        to={getPlayerPath(ap.playerID) || '#'}
                                                                        className={classes.playerLinkText}
                                                                    >
                                                                        {`${ap.name}${ap.defaultNumber ? ' #' + ap.defaultNumber : ''}`}
                                                                    </Link>
                                                                </span>
                                                            </React.Fragment>
                                                        ))}
                                                    </>
                                                ) : (data.details ? `${data.subtitle ? ' | ' : ''}${t('actions:assist')}${data.details}` : '')}
                                            </span>
                                        ) : ''
                                    }
                                />

                                <>
                                    <Chip
                                        size={"small"}
                                        avatar={
                                            <Avatar>
                                                {data.period}
                                            </Avatar>
                                        }
                                        label={convertMillisToTime(data.time)}
                                    />
                                </>
                            </ListItem>
                        </List>

                    </Grid>

                    <Grid item xs={12} sm={6} >
                        <EventComments eventID={eventID} leagueID={leagueID} />
                    </Grid>

                </Grid>
            </ScrollBody>
        </>
    )
}
export default Event
